import {
  anyContainingPermission,
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { buildPermission, getCustomerId } from "common/utils/auth";
import {
  Button,
  capitalize,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Typography,
} from "@mui/material";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { EditEquipmentButton } from "./EditEquipmentButton";
import { EmptyGarage } from "./EmptyGarage";
import { EquipmentLibraryCard } from "portal/components/atk/EquipmentLibraryCard";
import { EquipmentSubpath, Path } from "portal/utils/routing";
import { getTractorLibraryCardData } from "portal/components/atk/TractorLibraryCardData";
import { Header } from "portal/components/header/Header";
import { Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { Tractor } from "protos/atk/autotractor";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DetailsIcon from "@mui/icons-material/MoreHoriz";
import React, { FC, useState } from "react";

interface TractorLibraryProps {}

export const _TractorLibraryPage: FC<TractorLibraryProps> = () => {
  const { t } = useTranslation();
  const { user } = useSelf();
  const selfCustomerId = getCustomerId(user);
  const [customerId, setCustomerId] = useState(selfCustomerId);

  const canSwitchCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  const canCreateOrEditArchetypes = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  // XXX: customer access to create implements is temporarily disabled until
  // this flow gets a UX pass per NickT.
  const canCreateTractor = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  const navigate = useNavigate();

  const { data: searchResult, isFetching: tractorsFetching } = useQueryPopups(
    atkEquipmentApi.endpoints.searchTractors.useQuery(
      {
        customerId: customerId?.toString(),
      },
      { skip: !customerId }
    )
  );

  let libraryBody: JSX.Element | undefined;
  const [detailsMenu, setDetailsMenu] = useState<
    { anchorEl: Element; tractor: Tractor } | undefined
  >();
  const [showDeleteModal, setShowDeleteModal] = useState<Tractor | undefined>();

  const [deleteTractor] = useMutationPopups(
    atkEquipmentApi.endpoints.deleteTractor.useMutation(),
    {
      success: capitalize(
        t("utils.actions.deletedLong", {
          subject: t("models.equipment.tractor", { count: 1 }),
        })
      ),
    }
  );

  if (tractorsFetching) {
    libraryBody = (
      <div className="w-full mt-20 flex flex-row gap-4 justify-center items-center">
        <CircularProgress variant="indeterminate" />
        <Typography variant="h5">
          {t("components.Loading.placeholder")}
        </Typography>
      </div>
    );
  } else if (searchResult?.tractors.length) {
    libraryBody = (
      <div className="grid grid-flow-row gap-4 grid-cols-2">
        {searchResult.tractors.map((tractor) => (
          <EquipmentLibraryCard
            key={tractor.id}
            data={getTractorLibraryCardData(t, tractor)}
            ActionButtonIcon={DetailsIcon}
            onActionButtonClick={(e) =>
              setDetailsMenu({ anchorEl: e.currentTarget, tractor })
            }
          />
        ))}
        <Menu
          open={detailsMenu !== undefined}
          anchorEl={detailsMenu?.anchorEl}
          onClose={() => setDetailsMenu(undefined)}
        >
          <MenuList>
            <EditEquipmentButton
              variant="menuItem"
              type="tractor"
              equipmentId={detailsMenu?.tractor.id ?? ""}
            />
            {canCreateOrEditArchetypes && detailsMenu?.tractor.archetype && (
              <EditEquipmentButton
                variant="menuItem"
                type="tractor-archetype"
                equipmentId={detailsMenu.tractor.archetype.id}
              />
            )}
            <MenuItem onClick={() => setShowDeleteModal(detailsMenu?.tractor)}>
              <ListItemIcon>
                <DeleteForeverIcon />
              </ListItemIcon>
              <ListItemText>{t("utils.actions.delete")}</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
        {showDeleteModal && (
          <ConfirmationDialog
            destructive
            yesText={t("utils.actions.deleteLong", {
              subject: t("models.equipment.tractor_one"),
            })}
            description={t("components.ConfirmationDialog.delete.description", {
              subject: showDeleteModal.name,
            })}
            onYes={async () => {
              const res = await deleteTractor(showDeleteModal);
              if (!res.error) {
                setShowDeleteModal(undefined);
                setDetailsMenu(undefined);
              }
            }}
            onClose={() => setShowDeleteModal(undefined)}
          />
        )}
      </div>
    );
  } else {
    libraryBody = <EmptyGarage />;
  }

  return (
    <Page>
      <Header
        title={t("views.autotractor.equipment.tractorLibrary")}
        parentLink={Path.EQUIPMENT}
      />
      <div className="flex flex-col gap-4">
        <div className="flex">
          <Typography variant="h4" className="self-center">
            {t("views.autotractor.equipment.tractorLibrary")}
          </Typography>
          <div className="flex-1" />
          {canSwitchCustomers && (
            <CustomerSelector
              value={customerId}
              onChange={setCustomerId}
              className="w-1/5 mx-4"
            />
          )}
          <div className="flex gap-2">
            {canCreateTractor && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() =>
                  navigate(
                    `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_TRACTOR}`
                  )
                }
              >
                {t("models.equipment.tractor", { count: 1 })}
              </Button>
            )}
            {canCreateOrEditArchetypes && (
              <Button
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_TRACTOR_ARCHETYPE}`
                  )
                }
              >
                {t("views.autotractor.equipment.tractors.newTractorArchetype")}
              </Button>
            )}
          </div>
        </div>
        {libraryBody}
      </div>
    </Page>
  );
};

export const TractorLibraryPage = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.all
      ),
    ],
  },
  _TractorLibraryPage,
  <UnauthorizedPage />
);
